// PLP related-colour swatches — hover preview.
//
// Mirrors the legacy behaviour: hovering a colour swatch previews that colour's image over
// the card gallery and retargets the card's links to that colour variant. The overlay
// (`.card-gallery__color-swap`) holds TWO <img> layers that ping-pong: the next variant is
// loaded into the back layer and cross-faded in over the front one, so moving straight from
// one swatch to another fades A→B directly (a single <img> would swap its src instantly).
// Event delegation so dynamically rendered cards work without re-binding.
const OVERLAY = '.card-gallery__color-swap';
const LAYER = '.card-gallery__color-swap-img';
const LINKS = '[ref="productCardLink"], [ref="cardGalleryLink"], [ref="productTitleLink"]';

let active = null;

function cardOf(swatch) {
  return swatch.closest('product-card, .product-card');
}

function activate(swatch) {
  const card = cardOf(swatch);
  if (!card) return;

  const overlay = card.querySelector(OVERLAY);
  const image = swatch.dataset.variantImage;
  if (overlay && image) {
    const layers = overlay.querySelectorAll(LAYER);
    // The visible ("front") layer keeps .is-active; load the next image into the other
    // ("back") layer so they cross-fade instead of swapping a shared <img>'s src.
    const front = overlay.querySelector(`${LAYER}.is-active`);
    const back = Array.from(layers).find((layer) => layer !== front) || layers[0];
    if (back) {
      // srcset + sizes (set in Liquid) let the browser pick a resolution matching the gallery.
      back.srcset = swatch.dataset.variantSrcset || '';
      back.src = image;
      // Reveal only once the image is decoded, so the cross-fade shows the image instead of
      // fading in a blank frame and snapping the image in when it finishes loading.
      const reveal = () => {
        if (active !== swatch) return;
        back.classList.add('is-active');
        if (front && front !== back) front.classList.remove('is-active');
        // Hide the gallery media so the cross-fade's midpoint dip reveals the card background,
        // not the selected variant's image (see .is-color-preview in base.css).
        overlay.closest('.card-gallery')?.classList.add('is-color-preview');
      };
      if (back.decode) {
        back.decode().then(reveal, reveal);
      } else {
        reveal();
      }
    }
  }

  const url = swatch.dataset.productUrl;
  if (url) {
    for (const link of card.querySelectorAll(LINKS)) {
      // Remember the original target once, so we can restore it on pointer-out.
      if (link.dataset.defaultHref == null) link.dataset.defaultHref = link.getAttribute('href') ?? '';
      link.setAttribute('href', url);
    }
  }
}

function deactivate(swatch) {
  const card = cardOf(swatch);
  if (!card) return;

  // Fade both layers out and the gallery media back in → back to the gallery image.
  card.querySelector('.card-gallery')?.classList.remove('is-color-preview');
  card.querySelectorAll(`${OVERLAY} ${LAYER}`).forEach((layer) => layer.classList.remove('is-active'));
  for (const link of card.querySelectorAll('[data-default-href]')) {
    link.setAttribute('href', link.dataset.defaultHref);
  }
}

document.addEventListener('pointerover', (event) => {
  const swatch = event.target.closest?.('[data-plp-related-color-swatch]');
  if (!swatch || swatch === active) return;
  // The selected swatch's colour is already the gallery's first image, so previewing it would
  // just cross-fade that image over itself. Instead, treat hovering it as "clear the preview":
  // fade any active preview back to the gallery, which already shows that first image while a
  // swatch (not the gallery itself) is hovered. With nothing active, this is a no-op — no
  // redundant fade.
  if (swatch.classList.contains('is-selected')) {
    if (active) deactivate(active);
    active = null;
    return;
  }
  active = swatch;
  activate(swatch);
});

document.addEventListener('pointerout', (event) => {
  const swatch = event.target.closest?.('[data-plp-related-color-swatch]');
  if (!swatch || swatch.contains(event.relatedTarget) || active !== swatch) return;
  // Moving straight to another swatch → the upcoming pointerover cross-fades to it; don't
  // revert (that would flash the gallery back in). Only revert on a real exit.
  const toSwatch = event.relatedTarget?.closest?.('[data-plp-related-color-swatch]');
  if (toSwatch && toSwatch !== swatch) return;
  active = null;
  deactivate(swatch);
});
